import { useState } from 'react';
import { Link } from 'react-router-dom';
import { format, startOfMonth, endOfMonth, endOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  ArrowLeft,
  CalendarRange,
  FileDown,
  FileSpreadsheet,
  Filter,
  Inbox,
  Landmark,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useFinance } from '@/contexts/FinanceContext';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import { useAccountStatement } from './useAccountStatement';
import { exportAccountStatementPdf } from './accountStatementExportPdf';
import { exportAccountStatementCsv } from './accountStatementExportCsv';

const formatBrl = (n: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);

function parseDateInput(value: string, fallback: Date): Date {
  if (!value) return fallback;
  const [year, month, day] = value.split('-').map((s) => Number.parseInt(s, 10));
  if (!year || !month || !day) return fallback;
  return new Date(year, month - 1, day);
}

function toDateInputValue(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

export function AccountStatementPage() {
  const { client } = useCurrentClient();
  const { accounts } = useFinance();

  const today = new Date();
  const [accountId, setAccountId] = useState<string | undefined>();
  const [fromDate, setFromDate] = useState<Date>(() => startOfMonth(today));
  const [toDate, setToDate] = useState<Date>(() => endOfMonth(today));

  const { account, statement, rows } = useAccountStatement(accountId, fromDate, toDate);

  const isCreditCard = account?.type === 'credit_card';
  const title = account
    ? `${isCreditCard ? 'Extrato do Cartão' : 'Extrato'} — ${account.name}`
    : 'Extrato da Conta';

  // Divergência entre o saldo calculado e o current_balance do banco: só faz
  // sentido comparar quando o período cobre até hoje (ou o futuro).
  const balanceMismatch =
    account &&
    statement &&
    endOfDay(toDate).getTime() >= today.getTime() &&
    Math.abs(statement.closingBalance - account.currentBalance) > 0.005;

  const handleExportPdf = () => {
    if (!account || !statement) return;
    exportAccountStatementPdf({
      accountName: account.name,
      isCreditCard: !!isCreditCard,
      clientName: client?.name ?? 'Cliente',
      fromDate,
      toDate,
      statement,
      rows,
    });
  };

  const handleExportCsv = () => {
    if (!account || !statement) return;
    exportAccountStatementCsv({ accountName: account.name, fromDate, toDate, statement, rows });
  };

  return (
    <div className="space-y-5">
      <div>
        <Button variant="ghost" size="sm" className="-ml-2 mb-2" asChild>
          <Link to="/app/relatorios">
            <ArrowLeft size={14} className="mr-1.5" />
            Relatórios
          </Link>
        </Button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-brand text-2xl font-bold text-foreground">{title}</h1>
            <p className="text-sm text-muted-foreground">
              {client?.name ? `${client.name} · ` : ''}
              Lançamentos realizados da conta no período, com saldo linha a linha.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handleExportCsv} disabled={!account}>
              <FileSpreadsheet size={14} className="mr-1.5" />
              Exportar CSV
            </Button>
            <Button
              onClick={handleExportPdf}
              disabled={!account}
              className="bg-accent font-semibold text-accent-foreground hover:bg-accent/90"
            >
              <FileDown size={14} className="mr-1.5" />
              Exportar PDF
            </Button>
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Filter size={14} />
            Filtros
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Conta *</Label>
              <Select value={accountId ?? ''} onValueChange={setAccountId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a conta" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">De</Label>
              <Input
                type="date"
                value={toDateInputValue(fromDate)}
                onChange={(e) => setFromDate(parseDateInput(e.target.value, fromDate))}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Até</Label>
              <Input
                type="date"
                value={toDateInputValue(toDate)}
                onChange={(e) => setToDate(parseDateInput(e.target.value, toDate))}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {!account || !statement ? (
        <Card>
          <CardContent className="p-12 text-center text-muted-foreground">
            <Landmark className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <p className="font-medium">Escolha uma conta para gerar o extrato.</p>
            <p className="mt-1 text-sm">
              O extrato mostra os lançamentos realizados (pagos/recebidos) e o saldo da conta.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Saldo anterior</p>
                <p className="mt-1 text-2xl font-bold text-foreground">
                  {formatBrl(statement.openingBalance)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Entradas</p>
                <p className="mt-1 text-2xl font-bold text-income">
                  {formatBrl(statement.totalIn)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Saídas</p>
                <p className="mt-1 text-2xl font-bold text-expense">
                  {formatBrl(statement.totalOut)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Saldo final</p>
                <p
                  className={`mt-1 text-2xl font-bold ${
                    statement.closingBalance < 0 ? 'text-expense' : 'text-foreground'
                  }`}
                >
                  {formatBrl(statement.closingBalance)}
                </p>
              </CardContent>
            </Card>
          </div>

          {balanceMismatch && (
            <p className="text-xs text-muted-foreground">
              Nota: o saldo final calculado difere do saldo atual da conta (
              {formatBrl(account.currentBalance)}). Pode haver lançamentos pagos com data futura ou
              ajustes manuais de saldo.
            </p>
          )}

          {statement.count === 0 ? (
            <Card>
              <CardContent className="p-12 text-center text-muted-foreground">
                <Inbox className="mx-auto mb-3 h-10 w-10 opacity-40" />
                <p className="font-medium">Nenhum lançamento realizado nesta conta no período.</p>
                <p className="mt-1 text-sm">O saldo anterior é igual ao saldo final.</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="flex items-center gap-2 border-b border-border bg-secondary/30 px-4 py-2 text-xs text-muted-foreground">
                  <CalendarRange size={12} />
                  {format(fromDate, 'dd/MM/yyyy', { locale: ptBR })} —{' '}
                  {format(toDate, 'dd/MM/yyyy', { locale: ptBR })}
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[100px]">Data</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead>Categoria</TableHead>
                      <TableHead className="text-right">Entrada</TableHead>
                      <TableHead className="text-right">Saída</TableHead>
                      <TableHead className="text-right">Saldo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow className="bg-secondary/40 font-medium">
                      <TableCell className="text-xs" />
                      <TableCell className="text-sm" colSpan={4}>
                        Saldo anterior
                      </TableCell>
                      <TableCell className="text-right text-sm font-semibold tabular-nums">
                        {formatBrl(statement.openingBalance)}
                      </TableCell>
                    </TableRow>
                    {rows.map((r) => (
                      <TableRow key={r.entry.tx.id}>
                        <TableCell className="text-xs">
                          {format(r.entry.refDate, 'dd/MM/yyyy', { locale: ptBR })}
                        </TableCell>
                        <TableCell
                          className="max-w-[280px] truncate text-sm"
                          title={r.entry.tx.description}
                        >
                          {r.entry.tx.description}
                          {r.counterpartAccountName && (
                            <span className="ml-1.5 text-xs text-muted-foreground">
                              {r.entry.direction === 'out' ? '→' : '←'} {r.counterpartAccountName}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {r.categoryName}
                        </TableCell>
                        <TableCell className="text-right text-sm font-medium tabular-nums text-income">
                          {r.entry.direction === 'in' ? formatBrl(r.entry.amount) : ''}
                        </TableCell>
                        <TableCell className="text-right text-sm font-medium tabular-nums text-expense">
                          {r.entry.direction === 'out' ? formatBrl(r.entry.amount) : ''}
                        </TableCell>
                        <TableCell
                          className={`text-right text-sm font-semibold tabular-nums ${
                            r.entry.runningBalance < 0 ? 'text-expense' : 'text-foreground'
                          }`}
                        >
                          {formatBrl(r.entry.runningBalance)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-secondary/40 font-medium">
                      <TableCell className="text-xs" />
                      <TableCell className="text-sm" colSpan={4}>
                        Saldo final
                      </TableCell>
                      <TableCell
                        className={`text-right text-sm font-semibold tabular-nums ${
                          statement.closingBalance < 0 ? 'text-expense' : 'text-foreground'
                        }`}
                      >
                        {formatBrl(statement.closingBalance)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
